import React, { useState, useEffect } from "react";
import { getFilterData } from "../../../../Api/Api";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from "@mui/material";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import { PrimaryTooltip } from "../../../../Components/StyledComponent";
import { FilterIconSvg } from "../../../../Features/Table/Icons";
import LocationFilter from "../../../BmsFaults/Analytics/DetailsFilters/LocationFilter";
import GlobalFilter from "../../../Overview/components/GlobalFilter";
import { useCanUseGlobalAdvancedFilter } from "../../../../Helper/Permissions/GlobalAdvancedFilter";
import AppCache from "../../../../Helper/Common/AppCache";
import RULCategoryFilter from "./Filters/RULCategoryFilter";
import RULCyclesFilter from "./Filters/RULCyclesFilter";
import StaticOptionsFilter from "../SOHDetails/Filters/StaticOptionsFilter";
import OperationalStatusFilter from "../../../Common/OperationalStatusFilter";
import { CELL_CHEMISTRY_FILTER_CONFIG, parseCellChemistryOptions } from "../utils/cellChemistryFilter";

// ─── Static option lists ──────────────────────────────────────────────────────
const CONFIG_FILTER_CONFIG = {
  type: "dynamic",
  queryParams: { table: "batteryConfigList", column: "configName" },
};
// Protocol options come from protocolMeta: org-scoped, but not narrowed to the batteries
// currently visible, so a protocol with no matching battery can appear in the list.
const PROTOCOL_FILTER_CONFIG = {
  type: "dynamic",
  queryParams: { table: "protocolMeta", column: "protocolName" },
};
const MANUFACTURING_YEAR_OPTIONS = [
  "2019", "2020", "2021", "2022", "2023", "2024",
];

// An org can relabel a filter by giving its globalFilters entry an object with a label
// instead of `true`, so the sidebar reads the label through here rather than hardcoding it.
const getFilterLabel = (config, defaultLabel) =>
  typeof config === "object" && config?.label ? config.label : defaultLabel;

// ─── Component ────────────────────────────────────────────────────────────────
const RULDetailsSettingsSidebar = ({
  isExpanded,
  onToggle,
  onFiltersChange,
  allOnboardingLocationsHierarchy = [],
  allFleets = [],
  selectedFleetIds = [],
  filtersConfig = {},
  filters = {},
}) => {
  const canUseGlobalAdvancedFilter = useCanUseGlobalAdvancedFilter();
  const globalFilterConfig = AppCache.getItem("globalFilter")?.find((f) => f.type === "unifiedGlobalFilter") || null;

  const [activeTab, setActiveTab] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [configOptions, setConfigOptions] = useState([]);
  const [protocolOptions, setProtocolOptions] = useState([]);
  const [cellChemistryOptions, setCellChemistryOptions] = useState([]);

  useEffect(() => {
    if (filtersConfig.cellChemistry) {
      getFilterData(CELL_CHEMISTRY_FILTER_CONFIG).then((res) => {
        setCellChemistryOptions(parseCellChemistryOptions(res?.response?.response?.filterData));
      });
    }
  }, [filtersConfig.cellChemistry]);

  useEffect(() => {
    if (filtersConfig.configuration) {
      getFilterData(CONFIG_FILTER_CONFIG).then((res) => {
        const data = res?.response?.response?.filterData || [];
        setConfigOptions(data.map((item) => item.id));
      });
    }
  }, [filtersConfig.configuration]);

  useEffect(() => {
    if (filtersConfig.protocol) {
      getFilterData(PROTOCOL_FILTER_CONFIG).then((res) => {
        const data = res?.response?.response?.filterData || [];
        setProtocolOptions(data.map((item) => item.value ?? item.id).filter(Boolean));
      });
    }
  }, [filtersConfig.protocol]);

  const handleLocationChange = (loc) => {
    setSelectedLocation(loc);
    if (onFiltersChange) onFiltersChange({ location: loc });
  };

  // GlobalFilter applies business segment, customer account and location through one
  // onChange, and the two customer panels only travel as fleet ids.
  const handleGlobalFilterChange = ({ fleetIds, hierarchicalLocation }) => {
    setSelectedLocation(hierarchicalLocation);
    if (onFiltersChange) onFiltersChange({ location: hierarchicalLocation, fleets: fleetIds });
  };

  const handleResetAll = () => {
    setResetKey((k) => k + 1);
    setSelectedLocation([]);
    if (onFiltersChange) {
      onFiltersChange({
        operationalStatus: [],
        rulCategories: [],
        rulCycles: [],
        configuration: [],
        protocol: [],
        cellChemistry: [],
        location: [],
        fleets: [],
        manufacturingYear: [],
      });
    }
  };

  const hasAnyFilter =
    filtersConfig.location ||
    filtersConfig.fleetName ||
    filtersConfig.fleetCategory ||
    filtersConfig.operationalStatus ||
    filtersConfig.rulCategory ||
    filtersConfig.rulCycles ||
    filtersConfig.configuration ||
    filtersConfig.protocol ||
    filtersConfig.cellChemistry ||
    filtersConfig.manufacturingYear;

  return (
    <>
      {/* ── Collapse / expand toggle ──────────────────────────────────────────── */}
      <IconButton
        onClick={onToggle}
        sx={{
          position: "absolute",
          top: "2px",
          right: isExpanded ? "320px" : "0px",
          zIndex: 1000,
          backgroundColor: "transparent",
          width: "20px",
          height: "20px",
          transition: "right 0.3s ease-in-out",
          boxShadow: "none",
          "&:hover": { backgroundColor: "transparent" },
          "&:focus": { backgroundColor: "transparent" },
        }}
      >
        <PrimaryTooltip
          title={isExpanded ? "Collapse Panel" : "Expand Panel"}
          placement="bottom"
        >
          <KeyboardTabIcon
            sx={{
              fontSize: 20,
              color: "#494949",
              transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)",
              "&:hover": { color: "#6D72F6" },
            }}
          />
        </PrimaryTooltip>
      </IconButton>

      {/* ── Sidebar panel ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          width: isExpanded ? "320px" : "0px",
          transition: "width 0.3s ease-in-out",
          borderLeft: isExpanded ? "1px solid #E6E6E6" : "none",
          backgroundColor: "#FFFFFF",
          height: "100%",
          overflow: "hidden",
          flexShrink: 0,
          my: -2,
          mr: -3,
          position: "fixed",
          right: "24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            width: "320px",
            opacity: isExpanded ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
          }}
        >
          {/* ── "Settings" tab header ──────────────────────────────────────── */}
          <Box sx={{ px: 3, flexShrink: 0 }}>
            <Box sx={{ mx: -3 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{
                  minHeight: "40px",
                  "& .MuiTab-root": {
                    minHeight: "40px",
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#8D8D8D",
                    p: 2,
                    "&.Mui-selected": { color: "#494949" },
                  },
                  "& .MuiTabs-indicator": { backgroundColor: "#494949" },
                }}
              >
                <Tab label="Settings" />
              </Tabs>
            </Box>
            <Divider sx={{ borderColor: "#E6E6E6", mt: 0, mx: -3 }} />
          </Box>

          {/* ── Scrollable filter content ──────────────────────────────────── */}
          <Box
            sx={{
              flex: 1,
              overflowY: "scroll",
              overscrollBehavior: "contain",
              pb: 3,
              "&::-webkit-scrollbar": { width: "6px" },
            }}
          >
            {hasAnyFilter && (
              <Accordion
                defaultExpanded
                sx={{
                  boxShadow: "none",
                  "&:before": { display: "none" },
                  "&.Mui-expanded": { mt: 0, pb: 3 },
                }}
              >
                <AccordionSummary
                  expandIcon={<KeyboardDoubleArrowDownIcon />}
                  sx={{
                    "&.Mui-expanded": { minHeight: "auto" },
                    "& .MuiAccordionSummary-content": { margin: "20px 0" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      pr: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "4px",
                          backgroundColor: "#EFF0FF",
                          p: 0.75,
                        }}
                      >
                        <FilterIconSvg sx={{ color: "#4D44D8" }} />
                      </Box>
                      <Typography
                        variant="fw700s14c3D4B5A"
                        sx={{ color: "#2E2E2E", lineHeight: "20px" }}
                      >
                        Filters
                      </Typography>
                    </Box>
                    <Typography
                      variant="fw500s12lh246D72F6"
                      onClick={(e) => { e.stopPropagation(); handleResetAll(); }}
                      sx={{ cursor: "pointer", "&:hover": { color: "#4D44D8" } }}
                    >
                      Reset
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ paddingTop: 0 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>

                    {/* The group carries all three dimensions, so any one of the
                        three keys turns it on. Gating on location alone would have
                        dropped customer filtering for an org that had configured
                        fleetName/fleetCategory and not location, with nothing in
                        its place and no way to configure one without the other. */}
                    {(filtersConfig.location ||
                      filtersConfig.fleetName ||
                      filtersConfig.fleetCategory) && (
                      canUseGlobalAdvancedFilter && globalFilterConfig ? (
                        <GlobalFilter
                          key={`customerLocation-${resetKey}`}
                          allFleets={allFleets || []}
                          allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
                          selectedFleetIds={selectedFleetIds || []}
                          selectedLocations={filters.location || []}
                          direction="vertical"
                          onChange={handleGlobalFilterChange}
                        />
                      ) : (
                        <LocationFilter
                          key={`location-${resetKey}`}
                          locations={allOnboardingLocationsHierarchy}
                          selectedLocations={selectedLocation}
                          onLocationChange={handleLocationChange}
                        />
                      )
                    )}

                    {filtersConfig.operationalStatus && (
                      <OperationalStatusFilter
                        key={`operationalStatus-${resetKey}`}
                        selectedOperationalStatus={filters.operationalStatus || []}
                        onOperationalStatusChange={(vals) =>
                          onFiltersChange && onFiltersChange({ operationalStatus: vals })
                        }
                      />
                    )}

                    {filtersConfig.rulCategory && (
                      <RULCategoryFilter
                        key={`rulCategories-${resetKey}`}
                        onChange={(vals) =>
                          onFiltersChange && onFiltersChange({ rulCategories: vals })
                        }
                      />
                    )}

                    {filtersConfig.rulCycles && (
                      <RULCyclesFilter
                        key={`rulCycles-${resetKey}`}
                        onChange={(vals) =>
                          onFiltersChange && onFiltersChange({ rulCycles: vals })
                        }
                      />
                    )}

                    {filtersConfig.configuration && (
                      <StaticOptionsFilter
                        key={`configuration-${resetKey}`}
                        title="Configuration"
                        options={configOptions}
                        selectedValues={filters.configuration || []}
                        onChange={(vals) =>
                          onFiltersChange && onFiltersChange({ configuration: vals })
                        }
                      />
                    )}

                    {filtersConfig.cellChemistry && cellChemistryOptions.length > 0 && (
                      <StaticOptionsFilter
                        key={`cellChemistry-${resetKey}`}
                        title={getFilterLabel(filtersConfig.cellChemistry, "Cell Chemistry")}
                        options={cellChemistryOptions}
                        selectedValues={filters.cellChemistry || []}
                        onChange={(vals) =>
                          onFiltersChange && onFiltersChange({ cellChemistry: vals })
                        }
                      />
                    )}

                    {filtersConfig.protocol && protocolOptions.length > 0 && (
                      <StaticOptionsFilter
                        key={`protocol-${resetKey}`}
                        title={getFilterLabel(filtersConfig.protocol, "Protocol")}
                        options={protocolOptions}
                        selectedValues={filters.protocol || []}
                        onChange={(vals) =>
                          onFiltersChange && onFiltersChange({ protocol: vals })
                        }
                      />
                    )}

                    {filtersConfig.manufacturingYear && (
                      <StaticOptionsFilter
                        key={`manufacturingYear-${resetKey}`}
                        title="Manufacturing Year"
                        options={MANUFACTURING_YEAR_OPTIONS}
                        selectedValues={filters.manufacturingYear || []}
                        onChange={(vals) =>
                          onFiltersChange && onFiltersChange({ manufacturingYear: vals })
                        }
                      />
                    )}

                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default RULDetailsSettingsSidebar;
